import {
  PurchaseOption,
  WayPurchaseOptionCard,
  WayPurchaseSelectionCardVm,
  WayPurchaseSelectionDisplayVm,
  WayShopPurchaseOption,
} from '../models/way-purchase.models';
import {
  resolveWayPurchaseSubtitle,
  formatPrice,
  formatPurchasedWithSource,
  formatWashesAvailable,
  pickString,
  readRecord,
} from './way-purchase-display.helpers';
import {
  resolveWayPurchaseOptionPlateValue,
  wayPurchaseOptionNeedsPlateEntry,
} from './way-purchase-plate.util';
import { isRedeemableWayPurchase } from './way-purchase-lookup-orchestration.util';

function isShopOption(option: PurchaseOption): option is WayShopPurchaseOption {
  return option.source === 'shop';
}

function isWayOption(option: PurchaseOption): option is WayPurchaseOptionCard {
  return option.source === 'way';
}

function resolveWashTypeLabel(purchaseType?: string | null, redeemEvent?: string | null): string {
  const normalized = String(purchaseType ?? '').trim().toLowerCase();
  if (normalized === 'membership') {
    return 'Membership';
  }
  if (normalized === 'single_wash') {
    return 'Single Wash';
  }
  if (normalized === 'prepaid') {
    return 'Prepaid';
  }

  const event = String(redeemEvent ?? '').trim().toLowerCase();
  if (event === 'active-subscription') {
    return 'Membership';
  }
  if (event === 'order-success') {
    return 'Single Wash';
  }
  if (event === 'redeem-prepaid-purchase') {
    return 'Prepaid';
  }
  return '—';
}

function formatWashesAvailableLabel(
  purchaseType: string | null | undefined,
  washesIncluded: number | null | undefined,
  washesRedeemed: number | null | undefined,
  washesRemaining: number | null | undefined = undefined
): string {
  const type = String(purchaseType ?? '').trim().toLowerCase();
  if (type === 'single_wash') {
    return '1';
  }

  const available = formatWashesAvailable(washesIncluded, washesRedeemed, washesRemaining);
  if (available === 'Unlimited') {
    return 'Unlimited';
  }
  return `${available} left`;
}

function buildCardId(option: PurchaseOption): string {
  if (isShopOption(option)) {
    return `shop:${pickString(option.order_id) || option.redeem_event}`;
  }
  return `way:${pickString(option.purchase?.way_pass_id)}`;
}

function isOrderCanceled(orderStatus?: string | null, forceCanceled = false): boolean {
  if (forceCanceled) {
    return true;
  }
  return String(orderStatus ?? '').trim().toLowerCase() === 'cancelled';
}

function buildShopCardVm(
  option: WayShopPurchaseOption,
  canceledOrderIds: Set<string>
): WayPurchaseSelectionCardVm {
  const orderId = pickString(option.order_id);
  const isCanceled = isOrderCanceled(option.order_status, orderId ? canceledOrderIds.has(orderId) : false);

  return {
    id: buildCardId(option),
    option,
    packageName: pickString(option.package_name) || '—',
    customerName: '',
    showCustomerName: false,
    customerEmail: '',
    showCustomerEmail: false,
    customerPhone: '',
    showCustomerPhone: false,
    showInShopChip: true,
    washTypeLabel: resolveWashTypeLabel(option.purchase_type, option.redeem_event),
    washesAvailableDisplay: formatWashesAvailableLabel(
      option.purchase_type,
      option.washes_included,
      option.washes_redeemed
    ),
    priceDisplay: formatPrice(option.price_paid),
    purchasedDisplay: formatPurchasedWithSource(option.purchased_at, 'Shop'),
    isCanceled,
    isUnavailable: false,
    unavailableReason: '',
    canUse: !isCanceled,
    canCancel: !isCanceled && !!orderId,
    needsPlateEntry: false,
    licensePlateDisplay: '',
    showPlateInput: false,
    showLicensePlate: false,
  };
}

function buildWayCardVm(
  data: Record<string, any> | null | undefined,
  option: WayPurchaseOptionCard
): WayPurchaseSelectionCardVm {
  const purchase = option.purchase ?? {};
  const purchaseType = String(purchase.purchase_type ?? '').trim().toLowerCase();
  const washesIncluded =
    purchaseType === 'single_wash' ? 1 : purchase.washes_included;
  const canUse = isRedeemableWayPurchase(purchase);
  const unavailableReason = canUse
    ? ''
    : pickString(purchase.unredeemable_reason, purchase.status) ||
      'This Way purchase cannot be redeemed';
  const needsPlateEntry = canUse && wayPurchaseOptionNeedsPlateEntry(data, option);
  const plate = resolveWayPurchaseOptionPlateValue(option);
  const customerName = pickString(purchase.customer_name);
  const customerEmail = pickString(purchase.customer_email);
  const customerPhone = pickString(purchase.customer_phone);

  return {
    id: buildCardId(option),
    option,
    packageName: pickString(purchase.package_name) || '—',
    customerName: customerName || '—',
    showCustomerName: !!customerName,
    customerEmail: customerEmail || '—',
    showCustomerEmail: !!customerEmail,
    customerPhone: customerPhone || '—',
    showCustomerPhone: !!customerPhone,
    showInShopChip: false,
    washTypeLabel: resolveWashTypeLabel(purchase.purchase_type),
    washesAvailableDisplay: formatWashesAvailableLabel(
      purchase.purchase_type,
      washesIncluded,
      purchase.washes_redeemed,
      purchase.washes_remaining
    ),
    priceDisplay: formatPrice(purchase.price_paid),
    purchasedDisplay: formatPurchasedWithSource(purchase.purchased_at, 'Way'),
    isCanceled: false,
    isUnavailable: !canUse,
    unavailableReason,
    canUse,
    canCancel: false,
    needsPlateEntry,
    licensePlateDisplay: plate || '—',
    showPlateInput: needsPlateEntry,
    showLicensePlate: !!plate,
  };
}

export function readPurchaseOptions(
  data: Record<string, any> | null | undefined
): PurchaseOption[] {
  const row = readRecord(data);
  if (!row) {
    return [];
  }
  const raw = row['purchase_options'];
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map(item => readRecord(item))
    .filter((item): item is Record<string, any> => !!item)
    .filter(item => item['source'] === 'shop' || item['source'] === 'way')
    .map(item => {
      if (item['source'] === 'shop') {
        return item as any as WayShopPurchaseOption;
      }
      const purchase = readRecord(item['purchase']);
      return {
        source: 'way',
        purchase: (purchase ?? {}) as WayPurchaseOptionCard['purchase'],
        local_package_id: item['local_package_id'] as string | null | undefined,
      } satisfies WayPurchaseOptionCard;
    });
}

export function buildWayPurchaseSelectionDisplayVm(
  data: Record<string, any> | null | undefined,
  canceledOrderIds = new Set<string>()
): WayPurchaseSelectionDisplayVm | null {
  const options = readPurchaseOptions(data);
  if (!options.length) {
    return null;
  }

  const cards = options.map(option => {
    if (isShopOption(option)) {
      return buildShopCardVm(option, canceledOrderIds);
    }
    return buildWayCardVm(data, option);
  });

  return {
    subtitle: resolveWayPurchaseSubtitle(readRecord(data)),
    cards,
  };
}
